import { Component, inject, OnInit } from "@angular/core";
import { MatCardModule } from "@angular/material/card";
import { RouterOutlet } from "@angular/router";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { createSelector, select, Store } from "@ngrx/store";
import * as _ from "lodash";
import { filter } from "rxjs/operators";

import { AccountStatus } from "./definitions/account-status";
import { AuthEventsService } from "./services/auth/auth-events.service";
import { MessagingWebSocketService } from "./services/messaging-websocket.service";
import { OrderStatusWebSocketService } from "./services/order-status-websocket.service";
import { ReloadOnUpdateService } from "./services/reload-on-update.service";
import { REGISTERED_ICONS } from "./shared/icon-registry";
import { AppState } from "./store/reducers";
import { MpCommonSelectors } from "./store/selectors/mp-common.selectors";
import { SelectUser } from "./store/selectors/user.selectors";

/** Auth state and account status together: both gate the app-wide sockets. */
const userAndStatus = createSelector(SelectUser, MpCommonSelectors.userStatus, (user, userStatus) => ({
	user,
	userStatus
}));

@Component({
	selector: "app-root",
	template: ` <router-outlet></router-outlet> `,
	imports: [MatCardModule, RouterOutlet]
})
export class AppComponent implements OnInit {
	protected readonly accountStatus = AccountStatus;
	userAuthenticated: boolean;

	private readonly authEvents = inject(AuthEventsService);
	private readonly messagingWebSocketService = inject(MessagingWebSocketService);
	private readonly orderStatusWebSocketService = inject(OrderStatusWebSocketService);

	constructor(
		private store: Store<AppState>,
		reloadOnUpdateService: ReloadOnUpdateService,
		private library: FaIconLibrary
	) {
		reloadOnUpdateService.initialize();
		// Hook for ngrx manipulation with cypress needed when bypassing login
		if (_.has(window, "Cypress")) {
			_.set(window, "store", this.store);
		}
	}

	ngOnInit() {
		this.addIcons();
		this.authEvents.start();
		this.store
			.pipe(
				select(userAndStatus),
				filter(({ user, userStatus }) => !user.loading && !userStatus.loading)
			)
			.subscribe(({ user, userStatus }) => {
				this.userAuthenticated = _.get(user, "auth", false);
				this.syncMessagingSocket(
					this.userAuthenticated,
					AccountStatus.isActive(userStatus.status)
				);
			});
	}

	/**
	 * Keeps the app-wide messaging WebSocket in lockstep with auth state.
	 * The access token is not held in the store, so it is pulled from the
	 * OIDC client (same source the HTTP interceptor uses) on each
	 * authenticated transition. Connect/disconnect are idempotent.
	 */
	private syncMessagingSocket(authenticated: boolean, accountActive: boolean): void {
		if (!authenticated) {
			this.messagingWebSocketService.disconnect();
			this.orderStatusWebSocketService.disconnect();
			return;
		}
		// The messaging socket's onopen dispatches message.reconcile(), which fetches
		// /messaging/conversation — a 403 for an account still awaiting activation. Opening the
		// socket at all would keep repeating that call through the reconnect backoff and the
		// visibilitychange listener, so wait until the account is active.
		if (accountActive) {
			this.messagingWebSocketService.connect();
		} else {
			this.messagingWebSocketService.disconnect();
		}
		this.orderStatusWebSocketService.connect();
	}

	private addIcons(): void {
		this.library.addIcons(...REGISTERED_ICONS);
	}
}
